import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { UserNotification } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { useTeam } from "~/contexts/TeamProvider";
import { useCableSubscription } from "~/hooks/useCableSubscription";

type NotificationsPage = { items: UserNotification[]; nextPage: number | null };
type NotificationsCache = InfiniteData<NotificationsPage>;

export function useNotificationsMetrics() {
  const [unreadCount, setUnreadCount] = useState(0);
  const queryClient = useQueryClient();
  const { teamId } = useTeam();

  const prependNewItems = async () => {
    if (teamId == null) return;

    // use the most recent cached item's id as a cursor — if the cache is empty
    // (popup was never opened or cache was cleared), skip fetching since there's
    // nothing to prepend to and the next popup open will load fresh data anyway
    const cache = queryClient.getQueryData<NotificationsCache>(
      queryKeys.notifications(teamId, false),
    );
    const latestId = cache?.pages[0]?.items[0]?.id;
    if (!latestId) return;

    const { data } = await apiClient.notifications.notificationsList({
      s: "created_at DESC",
      q: { id_gt: latestId } as object,
    });

    const newItems = (
      data as unknown as { user_notifications: UserNotification[] }
    ).user_notifications;
    if (newItems.length === 0) return;

    const prepend = (prev: NotificationsCache | undefined) =>
      prev && {
        ...prev,
        pages: [
          { ...prev.pages[0], items: [...newItems, ...prev.pages[0].items] },
          ...prev.pages.slice(1),
        ],
      };

    queryClient.setQueryData<NotificationsCache>(
      queryKeys.notifications(teamId, false),
      prepend,
    );
    queryClient.setQueryData<NotificationsCache>(
      queryKeys.notifications(teamId, true),
      prepend,
    );
  };

  // reset immediately when team changes — safe to do synchronously since
  // WebSocket messages arrive only via async IO, after this render cycle
  useEffect(() => {
    setUnreadCount(0);
  }, [teamId]);

  useCableSubscription(
    "NotificationsChannel",
    teamId != null ? { team_id: teamId } : {},
    {
      received: (payload) => {
        if (payload.action === "notification-metrics") {
          setUnreadCount(payload.data.unread_count);
        } else if (payload.action === "notification-created") {
          setUnreadCount(payload.data.unread_count);
          void prependNewItems();
        }
      },
    },
  );

  return { unreadCount };
}
